import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  UiImageGalleryImage,
  UiImageGalleryImageInput,
  UiImageGalleryIndexChange,
} from './image-gallery.types';

export type {
  UiImageGalleryImage,
  UiImageGalleryImageInput,
  UiImageGalleryIndexChange,
};

const MAX_IMAGES = 5;

function toImage(item: UiImageGalleryImageInput): UiImageGalleryImage {
  return typeof item === 'string' ? { src: item } : item;
}

/**
 * Organismo: galería de fotos.
 *
 * Miniaturas verticales a la izquierda y foto principal a la derecha.
 * Presentacional. Máximo 5 fotos.
 */
@Component({
  selector: 'ui-image-gallery',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-gallery.component.html',
  styleUrl: './image-gallery.component.scss',
  host: { class: 'ui-image-gallery-host' },
})
export class UiImageGalleryComponent implements OnDestroy {
  readonly images = input<readonly UiImageGalleryImageInput[]>([]);
  readonly altPrefix = input('Imagen');
  readonly mobileNavigation = input(false);
  readonly mainImageInteractive = input(false);

  readonly indexChange = output<UiImageGalleryIndexChange>();
  readonly imageActivate = output<UiImageGalleryIndexChange>();

  protected readonly activeIndex = signal(0);
  protected readonly incomingIndex = signal<number | null>(null);
  protected readonly direction = signal<'next' | 'prev'>('next');
  protected readonly pushing = signal(false);
  private swipeStartX = 0;
  private swipeStartY = 0;
  private suppressActivationUntil = 0;
  private pushFrame = 0;

  protected readonly slides = computed(() =>
    this.images().map(toImage).slice(0, MAX_IMAGES),
  );

  protected readonly activeSlide = computed(() => {
    const slides = this.slides();
    if (!slides.length) return null;
    return slides[this.normalizedIndex()] ?? slides[0] ?? null;
  });

  protected readonly frames = computed(() => {
    const slides = this.slides();
    const currentIndex = this.normalizedIndex();
    const current = slides[currentIndex];
    if (!current) return [];
    const incomingIndex = this.incomingIndex();
    const incoming = incomingIndex == null ? null : slides[incomingIndex];
    if (!incoming || incomingIndex == null) {
      return [{ slide: current, index: currentIndex }];
    }
    const incomingFrame = { slide: incoming, index: incomingIndex };
    const currentFrame = { slide: current, index: currentIndex };
    return this.direction() === 'prev'
      ? [incomingFrame, currentFrame]
      : [currentFrame, incomingFrame];
  });

  ngOnDestroy(): void {
    cancelAnimationFrame(this.pushFrame);
  }

  protected setActiveIndex(index: number): void {
    const slides = this.slides();
    if (!slides.length || this.incomingIndex() !== null) return;
    const next = ((index % slides.length) + slides.length) % slides.length;
    if (next === this.normalizedIndex()) return;
    if (this.mobileNavigation()) {
      this.pushTo(next, next > this.normalizedIndex() ? 'next' : 'prev');
      return;
    }
    this.commitIndex(next);
  }

  protected previous(): void {
    this.pushBy(-1);
  }

  protected next(): void {
    this.pushBy(1);
  }

  protected activateMainImage(): void {
    if (Date.now() < this.suppressActivationUntil) return;
    const slide = this.activeSlide();
    if (!slide) return;
    this.imageActivate.emit({
      index: this.normalizedIndex(),
      src: slide.src,
    });
  }

  protected onSwipeStart(event: PointerEvent): void {
    if (!this.mobileNavigation()) return;
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
  }

  protected onSwipeEnd(event: PointerEvent): void {
    if (!this.mobileNavigation() || this.slides().length < 2) return;
    const deltaX = event.clientX - this.swipeStartX;
    const deltaY = event.clientY - this.swipeStartY;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.suppressActivationUntil = Date.now() + 500;
    if (deltaX < 0) {
      this.next();
      return;
    }
    this.previous();
  }

  protected onPushEnd(event: TransitionEvent): void {
    if (
      event.propertyName !== 'transform' ||
      event.target !== event.currentTarget
    ) {
      return;
    }
    const incoming = this.incomingIndex();
    if (incoming == null || !this.pushing()) return;
    this.pushing.set(false);
    this.incomingIndex.set(null);
    this.commitIndex(incoming);
  }

  protected frameAlt(slide: UiImageGalleryImage, index: number): string {
    return slide.alt ?? `${this.altPrefix()} ${index + 1}`;
  }

  protected thumbAlt(slide: UiImageGalleryImage, index: number): string {
    return slide.alt ?? `${this.altPrefix()} ${index + 1}`;
  }

  protected normalizedIndex(): number {
    const total = this.slides().length;
    if (!total) return 0;
    return ((this.activeIndex() % total) + total) % total;
  }

  private pushBy(delta: 1 | -1): void {
    const total = this.slides().length;
    if (total < 2 || this.incomingIndex() !== null) return;
    if (!this.mobileNavigation()) {
      this.setActiveIndex(this.normalizedIndex() + delta);
      return;
    }
    const next = (this.normalizedIndex() + delta + total) % total;
    this.pushTo(next, delta > 0 ? 'next' : 'prev');
  }

  private pushTo(index: number, direction: 'next' | 'prev'): void {
    cancelAnimationFrame(this.pushFrame);
    this.direction.set(direction);
    this.pushing.set(false);
    this.incomingIndex.set(index);
    this.pushFrame = requestAnimationFrame(() => {
      this.pushFrame = requestAnimationFrame(() => {
        if (this.incomingIndex() == null) return;
        this.pushing.set(true);
      });
    });
  }

  private commitIndex(index: number): void {
    const slides = this.slides();
    if (!slides.length) return;
    const next = ((index % slides.length) + slides.length) % slides.length;
    this.activeIndex.set(next);
    const slide = slides[next];
    if (slide) this.indexChange.emit({ index: next, src: slide.src });
  }
}
